export const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'BLOCKQUOTE']);

export function markdownToHtml(text: string): string {
	if (!text) return text;
	if (/<[a-z][\s\S]*>/i.test(text)) return text;

	let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

	const lines = html.split('\n');
	const result: string[] = [];
	let i = 0;

	while (i < lines.length) {
		const line = lines[i];

		if (/^### (.+)/.test(line)) {
			result.push(`<h3>${line.replace(/^### /, '')}</h3>`);
			i++;
		} else if (/^## (.+)/.test(line)) {
			result.push(`<h2>${line.replace(/^## /, '')}</h2>`);
			i++;
		} else if (/^# (.+)/.test(line)) {
			result.push(`<h1>${line.replace(/^# /, '')}</h1>`);
			i++;
		} else if (/^- (.+)/.test(line) || /^\* (.+)/.test(line)) {
			const ulItems: string[] = [];
			while (i < lines.length && (/^- (.+)/.test(lines[i]) || /^\* (.+)/.test(lines[i]))) {
				ulItems.push(`<li>${lines[i].replace(/^[-*] /, '')}</li>`);
				i++;
			}
			result.push(`<ul>${ulItems.join('')}</ul>`);
		} else if (/^\d+\. (.+)/.test(line)) {
			const olItems: string[] = [];
			while (i < lines.length && /^\d+\. (.+)/.test(lines[i])) {
				olItems.push(`<li>${lines[i].replace(/^\d+\. /, '')}</li>`);
				i++;
			}
			result.push(`<ol>${olItems.join('')}</ol>`);
		} else if (line.trim() === '') {
			i++;
		} else {
			let paragraph = line;
			i++;
			while (
				i < lines.length &&
				lines[i].trim() !== '' &&
				!/^#{1,3} /.test(lines[i]) &&
				!/^[-*\d]/.test(lines[i])
			) {
				paragraph += ' ' + lines[i];
				i++;
			}
			result.push(`<p>${paragraph}</p>`);
		}
	}

	return result
		.join('')
		.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
		.replace(/__(.+?)__/g, '<strong>$1</strong>')
		.replace(/\*(.+?)\*/g, '<em>$1</em>')
		.replace(/_(.+?)_/g, '<em>$1</em>');
}

const hasVisibleText = (nodes: Node[]) =>
	nodes.some((node) => {
		if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').trim() !== '';
		const el = node as Element;
		return el.tagName !== 'BR' && (el.textContent ?? '').trim() !== '';
	});

const isEmptyLine = (el: Element) => (el.textContent ?? '').trim() === '';

function appendNormalizedBlocks(source: Node, target: Element) {
	const doc = target.ownerDocument;
	let inline: Node[] = [];

	const flushInline = () => {
		if (hasVisibleText(inline)) {
			const p = doc.createElement('p');
			inline.forEach((node) => p.appendChild(node.cloneNode(true)));
			target.appendChild(p);
		}
		inline = [];
	};

	for (const child of Array.from(source.childNodes)) {
		if (child.nodeType === Node.TEXT_NODE) {
			inline.push(child);
			continue;
		}
		if (child.nodeType !== Node.ELEMENT_NODE) continue;

		const el = child as Element;
		if (el.tagName === 'DIV' || el.tagName === 'P') {
			flushInline();
			if (isEmptyLine(el)) {
				const blankLine = doc.createElement('p');
				blankLine.appendChild(doc.createElement('br'));
				target.appendChild(blankLine);
			} else {
				appendNormalizedBlocks(el, target);
			}
		} else if (el.tagName === 'BLOCKQUOTE') {
			flushInline();
			const quote = doc.createElement('blockquote');
			appendNormalizedBlocks(el, quote);
			target.appendChild(quote);
		} else if (BLOCK_TAGS.has(el.tagName)) {
			flushInline();
			target.appendChild(el.cloneNode(true));
		} else {
			inline.push(el);
		}
	}

	flushInline();
}

export function normalizeEditorHtml(raw: string): string {
	if (!raw || raw === '<br>') return '';

	const temp = document.createElement('div');
	temp.innerHTML = raw;

	const out = document.createElement('div');
	appendNormalizedBlocks(temp, out);

	return (out.textContent ?? '').trim() === '' ? '' : out.innerHTML;
}
